import React from 'react';
import styled from 'styled-components';
import { FileText, Download, Calendar } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

const Container = styled.section`
  margin-top: 90px;
  min-height: calc(100vh - 90px);
  background: var(--background-light);
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const Title = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--primary-blue);
  text-align: center;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  text-align: center;
  color: var(--text-light);
  font-size: 1.1rem;
  margin-bottom: 4rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
`;

const DocumentSection = styled.div`
  background: var(--white);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  background: var(--primary-blue);
  color: var(--white);
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SectionTitle = styled.h2`
  font-family: var(--font-secondary);
  font-size: 1.5rem;
`;

const DocumentList = styled.div`
  padding: 1.5rem;
`;

const DocumentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }
`;

const DocumentInfo = styled.div`
  flex: 1;
`;

const DocumentTitle = styled.h3`
  color: var(--text-dark);
  margin-bottom: 0.25rem;
`;

const DocumentMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.85rem;
  color: var(--text-light);
`;

const DownloadButton = styled.button`
  background: var(--gold);
  color: var(--primary-blue);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: var(--transition);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }
`;

const Documents = () => {
  const { content } = useSiteContent('documents_page');
  const data = content || {};

  const textesJuridiques = Array.isArray(data.textesJuridiques) ? data.textesJuridiques : [];
  const rapports = Array.isArray(data.rapports) ? data.rapports : [];

  return (
    <Container>
      <Content>
        <Title>{data.title || 'Documents Officiels'}</Title>
        <Subtitle>
          {data.subtitle || 'Accédez aux textes juridiques, rapports et publications de la Médiature'}
        </Subtitle>

        <Grid>
          <DocumentSection>
            <SectionHeader>
              <FileText size={24} />
              <SectionTitle>{data.textesTitle || 'Textes Juridiques'}</SectionTitle>
            </SectionHeader>
            <DocumentList>
              {textesJuridiques.map((doc, idx) => (
                <DocumentItem key={`text-${idx}`}>
                  <DocumentInfo>
                    <DocumentTitle>{doc.title || ''}</DocumentTitle>
                    <DocumentMeta>
                      <Calendar size={14} />
                      {doc.date || ''}
                    </DocumentMeta>
                  </DocumentInfo>
                  <DownloadButton>
                    <Download size={18} />
                    {data.downloadLabel || 'Télécharger'}
                  </DownloadButton>
                </DocumentItem>
              ))}
            </DocumentList>
          </DocumentSection>

          <DocumentSection>
            <SectionHeader>
              <FileText size={24} />
              <SectionTitle>{data.rapportsTitle || 'Rapports & Études'}</SectionTitle>
            </SectionHeader>
            <DocumentList>
              {rapports.map((doc, idx) => (
                <DocumentItem key={`rapport-${idx}`}>
                  <DocumentInfo>
                    <DocumentTitle>{doc.title || ''}</DocumentTitle>
                    <DocumentMeta>
                      <Calendar size={14} />
                      {doc.date || ''}
                    </DocumentMeta>
                  </DocumentInfo>
                  <DownloadButton>
                    <Download size={18} />
                    {data.downloadLabel || 'Télécharger'}
                  </DownloadButton>
                </DocumentItem>
              ))}
            </DocumentList>
          </DocumentSection>
        </Grid>
      </Content>
    </Container>
  );
};

export default Documents;
